'use client';

/**
 * 全局WebSocket管理器
 * 使用BroadcastChannel实现多标签页共享，防止打开新标签页断开WS
 */

import { getToken } from '@/lib/api';
import { ChatWebSocketClient, WSMessage } from '@/lib/websocket/chat-client';

type ConnectionState = 'connecting' | 'connected' | 'disconnected' | 'error';
type MessageHandler = (message: WSMessage) => void;
type ConnectionHandler = (state: ConnectionState) => void;

class GlobalWebSocketManager {
  private client: ChatWebSocketClient | null = null;
  private broadcastChannel: BroadcastChannel | null = null;
  private connectionState: ConnectionState = 'disconnected';
  private messageHandlers: Set<MessageHandler> = new Set();
  private connectionHandlers: Set<ConnectionHandler> = new Set();
  private isPrimaryTab = false;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private activeTabCheckInterval: NodeJS.Timeout | null = null;
  private lastActivity = Date.now();

  constructor() {
    if (typeof window !== 'undefined') {
      // 创建BroadcastChannel用于标签页间通信
      this.broadcastChannel = new BroadcastChannel('websocket-manager');
      
      // 监听其他标签页的消息
      this.broadcastChannel.onmessage = (event) => {
        const { type, data } = event.data;
        
        // 异步处理消息，不阻塞UI
        requestAnimationFrame(() => {
          if (type === 'ws_message' && !this.isPrimaryTab) {
            // 非主标签页，转发消息给本地处理器
            setTimeout(() => {
              this.messageHandlers.forEach(handler => {
                try {
                  handler(data);
                } catch (error) {
                  console.error('Error in message handler:', error);
                }
              });
            }, 0);
          } else if (type === 'ws_state' && !this.isPrimaryTab) {
            // 同步连接状态
            this.connectionState = data.state;
            this.notifyConnectionHandlers();
          } else if (type === 'claim_primary') {
            // 其他标签页请求成为主标签页
            if (this.isPrimaryTab && this.client?.isConnected()) {
              // 当前是主标签页且已连接，拒绝请求
              this.broadcastChannel?.postMessage({
                type: 'primary_claimed',
                data: { tabId: this.getTabId() },
              });
            }
          } else if (type === 'primary_claimed') {
            // 其他标签页已经是主标签页
            if (data.tabId !== this.getTabId()) {
              this.isPrimaryTab = false;
            }
          }
        });
      };

      // 监听页面可见性变化 - 异步处理
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          // 异步执行，不阻塞
          requestAnimationFrame(() => {
            this.checkAndClaimPrimary();
          });
        }
      });

      // 监听页面卸载
      window.addEventListener('beforeunload', () => {
        if (this.isPrimaryTab) {
          // 主标签页关闭，通知其他标签页
          this.broadcastChannel?.postMessage({
            type: 'primary_closed',
          });
        }
      });

      // 定期检查活跃状态 - 异步执行
      this.activeTabCheckInterval = setInterval(() => {
        requestAnimationFrame(() => {
          const now = Date.now();
          // 更新最后活跃时间
          this.lastActivity = now;
          
          // 如果当前是主标签页，发送心跳（异步）
          if (this.isPrimaryTab && this.client?.isConnected()) {
            setTimeout(() => {
              this.client?.send('heartbeat', { timestamp: now });
            }, 0);
          }
        });
      }, 30000); // 30秒

      // 初始化时检查是否可以成为主标签页 - 异步执行
      requestAnimationFrame(() => {
        this.checkAndClaimPrimary();
      });
    }
  }

  private getTabId(): string {
    if (!window.sessionStorage.getItem('tabId')) {
      window.sessionStorage.setItem('tabId', `${Date.now()}-${Math.random()}`);
    }
    return window.sessionStorage.getItem('tabId') || '';
  }

  private checkAndClaimPrimary(): void {
    // 先检查是否有token，没有token就不尝试连接
    const token = getToken();
    if (!token) {
      // 没有token，不尝试成为主标签页
      return;
    }

    // 异步执行，不阻塞UI
    setTimeout(() => {
      // 请求成为主标签页
      this.broadcastChannel?.postMessage({
        type: 'claim_primary',
        data: { tabId: this.getTabId() },
      });

      // 等待一小段时间，如果没有其他标签页响应，则成为主标签页
      setTimeout(() => {
        // 再次检查token，确保在连接前仍然有效
        const currentToken = getToken();
        if (!currentToken) {
          console.debug('[全局WebSocket] 没有token，跳过连接');
          return;
        }

        if (!this.isPrimaryTab && !this.client) {
          this.isPrimaryTab = true;
          // 异步连接，不阻塞
          this.connect().catch(err => {
            // 如果是token错误，只记录debug日志，不显示错误
            if (err.message && err.message.includes('token')) {
              console.debug('[全局WebSocket] 连接失败：token不可用', err);
            } else {
              console.error('[全局WebSocket] 连接失败:', err);
            }
            // 连接失败时重置主标签页状态，允许其他标签页尝试
            this.isPrimaryTab = false;
          });
        }
      }, 100);
    }, 0);
  }

  /**
   * 连接到WebSocket服务器
   */
  connect(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      // 只有主标签页才建立真实连接
      if (!this.isPrimaryTab) {
        // 非主标签页，等待主标签页连接
        resolve();
        return;
      }

      // 如果已经连接或正在连接，不重复连接
      if (this.client && this.connectionState !== 'disconnected') {
        console.debug('[全局WebSocket] 已有连接，跳过重复连接');
        resolve();
        return;
      }

      const token = getToken();
      if (!token) {
        // 没有token时不报错，静默失败（可能是未登录状态）
        console.debug('[全局WebSocket] 没有可用的token，跳过连接（这可能是正常的，如果用户未登录）');
        resolve(); // 改为resolve而不是reject，避免错误日志
        return;
      }

      // 获取WebSocket URL（使用统一配置）
      const { getWebSocketURL } = await import('@/lib/config');
      const wsUrl = getWebSocketURL();

      // 异步更新连接状态，不阻塞
      requestAnimationFrame(() => {
        this.connectionState = 'connecting';
        this.notifyConnectionHandlers();
      });

      const client = new ChatWebSocketClient(wsUrl, token);
      this.client = client;

      // 连接状态监听 - 异步更新状态
      client.onConnect(() => {
        console.log('[全局WebSocket] 已成功连接');
        requestAnimationFrame(() => {
          this.connectionState = 'connected';
          this.notifyConnectionHandlers();
          resolve();
        });
      });

      client.onDisconnect(() => {
        console.log('[全局WebSocket] 连接已断开');
        requestAnimationFrame(() => {
          this.connectionState = 'disconnected';
          this.notifyConnectionHandlers();
          
          // 如果主标签页断开，尝试重连（异步），但要确保有token
          if (this.isPrimaryTab) {
            const token = getToken();
            if (token) {
              console.log('[全局WebSocket] 5秒后尝试自动重连...');
              this.reconnectTimer = setTimeout(() => {
                const currentToken = getToken();
                if (currentToken) {
                  this.connect().catch(err => {
                    console.error('[全局WebSocket] 重连失败:', err);
                  });
                } else {
                  console.debug('[全局WebSocket] 没有token，跳过重连');
                }
              }, 5000); // 增加到5秒，减少重连频率
            } else {
              console.debug('[全局WebSocket] 没有token，不尝试重连');
            }
          }
        });
      });

      client.onError(() => {
        requestAnimationFrame(() => {
          this.connectionState = 'error';
          this.notifyConnectionHandlers();
          reject(new Error('WebSocket connection error'));
        });
      });

      // 消息处理 - 异步处理，不阻塞
      client.onMessage('*', (message: WSMessage) => {
        // 使用 requestAnimationFrame 确保不阻塞UI渲染
        requestAnimationFrame(() => {
          // 主标签页收到消息，广播给其他标签页
          this.broadcastChannel?.postMessage({
            type: 'ws_message',
            data: message,
          });
          
          // 本地处理 - 使用 setTimeout 0 确保异步执行
          setTimeout(() => {
            this.messageHandlers.forEach(handler => {
              try {
                handler(message);
              } catch (error) {
                console.error('Error in message handler:', error);
              }
            });
          }, 0);
        });
      });

      // 连接 - 异步执行，不阻塞
      client.connect().catch(err => {
        requestAnimationFrame(() => {
          this.connectionState = 'error';
          this.notifyConnectionHandlers();
          reject(err);
        });
      });
    });
  }

  /**
   * 断开连接
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    if (this.client) {
      this.client.disconnect();
      this.client = null;
    }
    
    this.connectionState = 'disconnected';
    this.isPrimaryTab = false; // 重置主标签页状态
    this.notifyConnectionHandlers();
  }

  /**
   * 发送消息
   */
  send(type: string, data: unknown): boolean {
    if (this.isPrimaryTab && this.client?.isConnected()) {
      this.client.send(type, data);
      return true;
    }
    return false;
  }

  /**
   * 发送聊天消息
   * @param content 消息内容
   * @param roomId 聊天室ID，默认为1（公共聊天室）
   */
  sendMessage(content: string, roomId: number = 1): boolean {
    return this.send('message', {
      content,
      room_id: roomId,
      message_type: 'text',
    });
  }

  /**
   * 获取在线用户列表
   */
  getOnlineUsers(): void {
    this.send('get_online_users', {});
  }

  /**
   * 注册消息处理器
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /**
   * 注册连接状态处理器
   */
  onConnectionChange(handler: ConnectionHandler): () => void {
    this.connectionHandlers.add(handler);
    // 立即通知当前状态
    handler(this.connectionState);
    return () => {
      this.connectionHandlers.delete(handler);
    };
  }

  /**
   * 通知连接状态变化 - 异步执行，不阻塞UI
   */
  private notifyConnectionHandlers(): void {
    // 使用 requestAnimationFrame 确保不阻塞UI渲染
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.connectionHandlers.forEach(handler => {
          try {
            handler(this.connectionState);
          } catch (error) {
            console.error('Error in connection handler:', error);
          }
        });
        
        // 同步状态到其他标签页 - 异步发送
        if (this.broadcastChannel) {
          this.broadcastChannel.postMessage({
            type: 'ws_state',
            data: { state: this.connectionState },
          });
        }
      }, 0);
    });
  }

  /**
   * 获取连接状态
   */
  getState(): ConnectionState {
    return this.connectionState;
  }

  /**
   * 检查是否已连接
   */
  isConnected(): boolean {
    return this.connectionState === 'connected';
  }

  /**
   * 清理资源
   */
  destroy(): void {
    if (this.activeTabCheckInterval) {
      clearInterval(this.activeTabCheckInterval);
      this.activeTabCheckInterval = null;
    }
    
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    this.disconnect();
    
    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = null;
    }
  }
}

// 创建全局实例
export const globalWSManager = new GlobalWebSocketManager();

// 初始化WebSocket连接（在用户登录后调用）
export function initGlobalWebSocket(): Promise<void> {
  return globalWSManager.connect();
}

// 断开WebSocket连接（在用户登出时调用）
export function closeGlobalWebSocket(): void {
  globalWSManager.disconnect();
}





